// openEHR LANG model — BMM package org.openehr.lang.bmm.statement
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { BMM_TYPE } from "../_shared.ts";
import { EL_AGENT_CALL } from "./expression.ts";
import type { EL_BOOLEAN_EXPRESSION, EL_EXPRESSION, EL_PROCEDURE_AGENT, EL_VALUE_GENERATOR, EL_WRITABLE_VARIABLE } from "./expression.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Specialised form of Decision Table that allows only procedure call agents (lambdas) as the result of branches.
 */
export class BMM_ACTION_DECISION_TABLE {
}

/**
 * Abstract parent of statement types representing a locally defined routine body.
 */
export abstract class BMM_STATEMENT_ITEM {
}

/**
 * Abstract parent of 'statement' types that may be defined to implement BMM Routines.
 */
export abstract class BMM_STATEMENT extends BMM_STATEMENT_ITEM {
}

/**
 * Multi-branch conditional statement structure
 */
export class BMM_ACTION_TABLE extends BMM_STATEMENT {
  /**
   * A specialised decision table whose outputs can only be procedure agents. In execution, the matched agent will be invoked.
   */
  decision_table?: BMM_ACTION_DECISION_TABLE;
}

/**
 * Simple statement, i.e. statement with one logical element - a single expression, procedure call etc.
 */
export abstract class BMM_SIMPLE_STATEMENT extends BMM_STATEMENT {
}

/**
 * Statement type representing an assignment from a value-generating source to a writable entity, i.e. a variable reference or property.
 *
 * At the meta-model level, may be understood as an initialisation of an existing meta-model instance.
 */
export class BMM_ASSIGNMENT extends BMM_SIMPLE_STATEMENT {
  /**
   * The target variable on the notional left-hand side of this assignment.
   */
  target?: EL_VALUE_GENERATOR;
  /**
   * Source right hand side) of the assignment.
   */
  source?: EL_EXPRESSION;
}

/**
 * A statement that asserts the truth of its expression. If the expression evaluates to False the execution generates an exception (depending on run-time settings).
 *
 * May be rendered in syntax as \`assert condition\` or similar.
 */
export class BMM_ASSERTION extends BMM_SIMPLE_STATEMENT {
  /**
   * Boolean-valued expression of the assertion.
   */
  expression?: EL_BOOLEAN_EXPRESSION;
  /**
   * Internal storage for tag
   * @protected
   */
  protected _tag?: openehr_base.String;

  /**
   * Optional tag, typically used to designate design intention of the assertion, e.g. \`"Inv_all_members_valid"\`.
   */
  get tag(): string | undefined {
    return this._tag?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for tag.
   * Use this to access openehr_base.String methods.
   */
  get $tag(): openehr_base.String | undefined {
    return this._tag;
  }

  /**
   * Sets tag from either a primitive value or openehr_base.String wrapper.
   */
  set tag(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._tag = undefined;
    } else if (typeof val === "string") {
      this._tag = openehr_base.String.from(val);
    } else {
      this._tag = val;
    }
  }
}

/**
 * A call made on a closed procedure agent. The method in BMM via which external actions are achieved from within a program.
 */
export class BMM_PROCEDURE_CALL extends EL_AGENT_CALL {
  /**
   * The procedure agent being called.
   */
  override agent?: EL_PROCEDURE_AGENT = undefined;
}

/**
 * A statement 'block' corresponding to the programming language concept of the same name. May be used to establish scope in specific languages.
 */
export class BMM_STATEMENT_BLOCK extends BMM_STATEMENT_ITEM {
  /**
   * Child blocks of the current block.
   */
  items?: undefined;
}

/**
 * Declaration of a writable variable, associating a name with a type.
 */
export class BMM_DECLARATION extends BMM_SIMPLE_STATEMENT {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  result?: EL_WRITABLE_VARIABLE;
  /**
   * The declared type of the variable.
   */
  type?: BMM_TYPE;
}
